import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { setupProductXR } from '@theme/product-xr';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    queueMicrotask(() => {
      this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    });

    if (this.querySelector('[data-shopify-xr]')) {
      setupProductXR(this);
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const zoomDialog = this.refs.zoomDialogComponent;
    const wasZoomOpen = Boolean(zoomDialog?.refs.dialog?.open);
    const variantId = event.detail.resource?.id ? String(event.detail.resource.id) : null;
    const featuredMediaId = event.detail.resource?.featured_media?.id
      ? String(event.detail.resource.featured_media.id)
      : null;

    const targetIndex = this.#resolveVariantMediaIndex(newMediaGallery, { variantId, featuredMediaId });

    this.replaceWith(newMediaGallery);

    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const insertedGallery = /** @type {MediaGallery | null} */ (newMediaGallery);
        insertedGallery?.#activateMediaIndex(targetIndex, { restoreZoom: wasZoomOpen });
      });
    });
  };

  /**
   * @param {Element} galleryFragment
   * @param {{ variantId: string | null, featuredMediaId: string | null }} options
   * @returns {number}
   */
  #resolveVariantMediaIndex(galleryFragment, { variantId, featuredMediaId }) {
    const modelIndex = this.#findModelSlideIndex(galleryFragment, variantId);
    if (modelIndex >= 0) return modelIndex;

    return this.#findFeaturedMediaIndex(galleryFragment, featuredMediaId);
  }

  /**
   * @param {Element} galleryFragment
   * @param {string | null} variantId
   * @returns {number}
   */
  #findModelSlideIndex(galleryFragment, variantId) {
    if (!variantId) return -1;

    const slides =
      galleryFragment.querySelectorAll('slideshow-slide').length > 0
        ? galleryFragment.querySelectorAll('slideshow-slide')
        : galleryFragment.querySelectorAll('.dialog-zoomed-gallery > li');

    for (const [index, slide] of slides.entries()) {
      if (!(slide instanceof HTMLElement)) continue;
      if (!slide.classList.contains('product-media-container--model')) continue;

      const variantIds = slide.dataset.variantIds?.split(',').filter(Boolean) ?? [];
      if (variantIds.includes(variantId)) return index;
    }

    return -1;
  }

  /**
   * @param {Element} galleryFragment
   * @param {string | null} featuredMediaId
   * @returns {number}
   */
  #findFeaturedMediaIndex(galleryFragment, featuredMediaId) {
    if (!featuredMediaId) return 0;

    const slides =
      galleryFragment.querySelectorAll('slideshow-slide').length > 0
        ? galleryFragment.querySelectorAll('slideshow-slide')
        : galleryFragment.querySelectorAll('.dialog-zoomed-gallery > li');

    for (const [index, slide] of slides.entries()) {
      if (slide.id.includes(featuredMediaId)) {
        return index;
      }
    }

    return 0;
  }

  /**
   * @param {number} index
   * @param {{ restoreZoom: boolean }} options
   */
  #activateMediaIndex(index, { restoreZoom }) {
    this.slideshow?.select(index, undefined, { animate: false });

    const slide = this.#resolveSlideshowSlide(undefined, index);
    this.#syncMediaModeStripActiveState(slide);
    this.#autoplayModelSlide(slide, index);
    this.#syncActiveXRButton(slide);

    if (restoreZoom) {
      this.refs.zoomDialogComponent?.restoreAfterVariantUpdate(index);
    }

    if (this.querySelector('[data-shopify-xr]')) {
      setupProductXR(this);
    }
  }

  /**
   * Ensures the active slide's View in Space button targets the current variant model.
   * @param {Element | undefined} slide
   */
  #syncActiveXRButton(slide) {
    if (!(slide instanceof HTMLElement)) return;

    const modelId = this.dataset.currentXrModelId;
    if (!modelId) return;

    const button = slide.querySelector('[data-shopify-xr]');
    button?.setAttribute('data-shopify-model3d-id', modelId);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const { index, slide } = event.detail;
    const slideEl = this.#resolveSlideshowSlide(slide, index);
    this.#syncMediaModeStripActiveState(slideEl);
    this.#autoplayModelSlide(slideEl, index);
  };

  /**
   * @param {Element | undefined} slide
   * @param {number} index
   * @returns {Element | undefined}
   */
  #resolveSlideshowSlide(slide, index) {
    if (slide instanceof Element) return slide;
    const slides = this.slideshow?.slides;
    return typeof index === 'number' && Array.isArray(slides) ? slides[index] : undefined;
  }

  /** Syncs active state on Video and 360 View buttons only (Photos stays fixed). */
  #syncMediaModeStripActiveState(slide) {
    if (!(slide instanceof Element)) return;

    const isModel =
      slide.classList.contains('product-media-container--model') || slide.querySelector('product-model');
    const isImage =
      slide.classList.contains('product-media-container--image') || slide.querySelector('product-image');
    const isVideo =
      slide.classList.contains('product-media-container--video') ||
      slide.classList.contains('product-media-container--external_video') ||
      !!slide.querySelector('.product-media deferred-media video, .product-media deferred-media iframe');

    const videoBtn = this.querySelector('.media-gallery__mode[data-media-mode="video"]');
    const modelBtn = this.querySelector('.media-gallery__mode[data-media-mode="360"]');
    const imageBtn = this.querySelector('.media-gallery__mode[data-media-mode="photos"]');

    videoBtn?.classList.toggle('media-gallery__mode--active', isVideo);
    modelBtn?.classList.toggle('media-gallery__mode--active', isModel);
    imageBtn?.classList.toggle('media-gallery__mode--active', isImage);
  }

  /**
   * Starts model media when a model slide is selected.
   * @param {Element | undefined} slide
   * @param {number} index
   */
  #autoplayModelSlide(slide, index) {
    const resolvedSlide = slide ?? this.refs.media?.[index];
    if (!(resolvedSlide instanceof Element)) return;
    if (!resolvedSlide.classList.contains('product-media-container--model') && !resolvedSlide.querySelector('product-model'))
      return;

    const productModel = resolvedSlide.querySelector('product-model');
    if (!(productModel instanceof HTMLElement)) return;
    if (productModel.hasAttribute('data-media-loaded')) return;
    if (typeof productModel.showDeferredMedia !== 'function') return;

    productModel.showDeferredMedia();
  }

  /**
   * Opens the zoom dialog for a 3D model on desktop only.
   * On mobile, Shopify XR handles the View in Space button natively.
   *
   * @param {number} index - The index of the model media.
   * @param {PointerEvent} event - The pointer event.
   */
  openModelZoom(index, event) {
    if (isMobileBreakpoint()) return;

    event.preventDefault();
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
